import { makeStyles, tokens } from '@fluentui/react-components';
import { LAYOUT } from './scale';

/**
 * The app's surface language — every bordered or elevated box, defined once (#202).
 *
 * ── Why a `makeStyles` hook and not a `<Surface>` component ──
 * Six test files walk exact DOM ancestry to find what they assert on;
 * `RequestForm.detailsSections.test.tsx` resolves each of the five Details cards
 * as `getByText(heading, { selector: '.fui-Subtitle2' }).parentElement` and then
 * asserts Griffel atomic classes two levels further up. A wrapper component adds
 * a node and breaks those chains with a confusing message. A className hook adds
 * ZERO nodes, so the surface language can be rolled out without disturbing the
 * structure those tests depend on.
 *
 * ── What this replaces ──
 * The previous version defined `card`, and `grep -rn 'surfaces\.card'` returned
 * ZERO consumers. In its place the app carried fourteen hand-rolled card
 * definitions across three radii (6px/4px/4px), two elevations (shadow4/none)
 * and five paddings (`48px 24px`, `24px`, `16px`, `12px`, `8px 12px`) — the
 * single biggest reason the app read as unfinished rather than designed.
 *
 * ── The rule that does not bend ──
 * Every elevated surface carries BOTH a shadow and a border. Under
 * `teamsHighContrastTheme` the background collapses to pure black and shadows
 * become invisible; the border is the only thing left holding the shape.
 *
 * ── Radius ──
 * `borderRadiusXLarge` is **8px** and `borderRadiusLarge` is **6px** in the
 * installed `@fluentui/tokens` (read from the theme object at runtime).
 * fluent2.microsoft.design says otherwise and is wrong for this version; that
 * confusion is why every card in the app used to render at 6px.
 */

/** Card padding, exported because a few layouts need to subtract it. */
export const CARD_PADDING = tokens.spacingVerticalL;

export const useSurfaceStyles = makeStyles({
  /**
   * E1 — a resting object on the page. The default surface: cards, sections,
   * stat tiles, the wizard's step panels, dropzones.
   */
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow4,
    padding: tokens.spacingVerticalL,
  },

  /**
   * E1 → E2 on hover/focus-within. ONLY for a surface that is genuinely
   * clickable — the lift IS the affordance, so putting it on a static card
   * promises an interaction that does not exist.
   */
  cardInteractive: {
    cursor: 'pointer',
    transitionProperty: 'box-shadow',
    transitionDuration: tokens.durationFaster,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': { boxShadow: tokens.shadow8 },
    ':focus-within': { boxShadow: tokens.shadow8 },
    '@media (prefers-reduced-motion: reduce)': { transitionDuration: '0.01ms' },
  },

  /**
   * A card with no padding of its own, for content that draws its own — a
   * `<Table>` supplies its own cell padding.
   *
   * ── Why this clips rather than scrolls (CLAUDE.md log entry 57) ──
   * Entry 57 fixed a real, client-reported bug: this card used the `overflow:
   * hidden` shorthand purely to clip a table's square corners to its rounded
   * ones, and that silently swallowed the Dashboard's rightmost Actions column
   * — no scrollbar, no error. Its fix put `overflowX: 'auto'` here.
   *
   * That fix is kept in EFFECT and superseded in MECHANISM. `DataTable` now
   * owns a dedicated scroll element between this card and the table, so the
   * excess is absorbed one level in. The reason to prefer that: a scrollbar on
   * the card itself runs along the rounded edge and clips against the 8px
   * radius, whereas the inner element scrolls cleanly inside it. Entry 57's own
   * rule is what licenses `hidden` here — "reserve plain `hidden` for content
   * you can prove is never wider than its container" — and it IS provable:
   * `tableCard` has exactly two consumers, `DataTable` (which always supplies
   * the scroll element) and Dashboard's fixed-width loading skeleton.
   *
   * **So: never put a bare `<table>` in this card.** Render it through
   * `DataTable`, or the entry-57 bug comes straight back. Entry 57's regression
   * test still guards the outcome — it reads `overflow-x` on the table's parent,
   * which is now the scroll element, and still requires `auto`.
   *
   * Per axis rather than the `overflow` shorthand, because Griffel emits a
   * shorthand as ONE atomic class that an inner rule cannot partially override
   * (log entry 58), and the horizontal axis is precisely what `DataTable` needs
   * to hand to its own element.
   *
   * Deliberately carries NO margin. The previous version baked in
   * `marginTop: '16px'`, which compounded with whatever gap its parent already
   * had — landing at 24px inside a gap-8 section and 32px inside a gap-16 tab
   * panel. Vertical rhythm belongs to the page stack, never to the component.
   */
  tableCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow4,
    overflowX: 'hidden',
    overflowY: 'hidden',
  },

  /**
   * E0 — structure rather than an object. A panel nested INSIDE a card: a file
   * row, a verbatim D365 error block, a thumbnail, an undo toast's body.
   * Background3 and the 4px control radius, because an 8px corner inside an 8px
   * corner reads wrong.
   */
  inset: {
    backgroundColor: tokens.colorNeutralBackground3,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalM,
  },

  /**
   * E2 — a sticky action or decision bar. It needs its own shadow precisely
   * because it floats over content scrolling underneath: without one it shares
   * `colorNeutralBackground1` with every card and, scrolled over a subsection,
   * reads as part of that card rather than as a bar above it.
   */
  stickyBar: {
    position: 'sticky',
    bottom: 0,
    zIndex: 1,
    backgroundColor: tokens.colorNeutralBackground1,
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
    boxShadow: tokens.shadow8,
    padding: tokens.spacingVerticalL,
  },

  /**
   * The selected state for a card or tile. Colour ALONE is not enough — high
   * contrast discards the brand hue entirely — so this pairs a brand edge with a
   * border-WIDTH change, and every caller adds a word or glyph as well.
   */
  cardSelected: {
    // The brand TINT rather than `colorNeutralBackground1Selected`. The neutral
    // selected grey sits between the card white and the page canvas, so on
    // screen it reads as *disabled* rather than *chosen* — checked against a
    // real render. The tint is unambiguous, and body text on it measures
    // 13.21:1 in light and 12.58:1 in dark, both AAA.
    //
    // Colour is still not doing the work on its own: the border steps from
    // `strokeWidthThin` to `strokeWidthThick`, which survives high contrast
    // where the tint collapses to the canvas colour, and every caller adds
    // `aria-pressed` besides.
    backgroundColor: tokens.colorBrandBackground2,
    border: `${tokens.strokeWidthThick} solid ${tokens.colorBrandStroke1}`,
  },

  /** Pagination row — Previous/Next plus a "Showing X–Y of Z" caption. */
  pager: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
});

/**
 * Layout primitives — the "fill the Teams canvas without looking stretched" half.
 *
 * The rule these encode: wide space is filled by ADDING A COLUMN, never by
 * widening one. A table wants the whole canvas; a text input at 1800px is
 * unreadable. So a page declares which of its surfaces are fluid and which are
 * measured.
 *
 * `withRail`/`rail` — a measured main column with a companion rail stuck beside
 * it — used to be the third option here, for the New Request wizard's summary
 * panel and the reviewer's decision panel. Both were removed (#206, #209) in
 * favour of a full-width `fluid` column with the actions in a full-width bottom
 * `ActionBar` instead: the rail only ever restated counts already visible in
 * the content beside it, so losing it dropped no information, only the
 * duplicate and the dead margin it left on a wide Teams tab. Re-add it here,
 * deliberately, if a future screen genuinely needs a companion rail again —
 * do not leave it as dead code for one that no longer does.
 */
export const useLayoutStyles = makeStyles({
  /** The vertical rhythm of a page. One gap value, owned here, set nowhere else. */
  stack: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },

  /** The page gutter: 24px on desktop, 16px on mobile where width is precious. */
  page: {
    padding: tokens.spacingVerticalXXL,
    '@media (max-width: 700px)': { padding: tokens.spacingVerticalL },
  },

  /** Fills the tab canvas. Tables, queues, stat-tile rows — capped by nothing. */
  fluid: { width: '100%' },

  /** Caps at ~75ch. Form fields, body copy, empty-state text, dialog bodies. */
  measured: { width: '100%', maxWidth: LAYOUT.measure },

  /**
   * A row of equal tiles that reflows rather than scrolling sideways.
   *
   * CAPPED, and that cap is the whole point. "Fill the Teams canvas" applies to
   * a table, which has data to put in the width; it does not apply to four
   * counters. Uncapped on a 1440px canvas these measured **332px per tile** for
   * a two-digit number — the stretched look the design brief explicitly rules
   * out. The cap holds them at a sensible size and lets the row sit at the
   * leading edge, which is also how wireframe R1 draws them.
   *
   * `LAYOUT.tileRowMax` is four tiles plus their three gaps, so the row caps
   * exactly when the tiles reach their intended width and never mid-tile.
   */
  tileRow: {
    display: 'grid',
    gap: tokens.spacingVerticalL,
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    maxWidth: LAYOUT.tileRowMax,
    // Wireframe S1a: the four counters go 4-up → 2×2 on a phone, never 1-up, so
    // the block stays scannable without becoming a column of its own.
    '@media (max-width: 700px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: tokens.spacingVerticalM,
    },
  },
});

/**
 * The visually-hidden clip technique, in one place.
 *
 * The only legitimately off-grid values in the app (`1px`, `-1px`). It was
 * duplicated across two pages; keeping it here means the 4px-grid rule stays a
 * greppable invariant with exactly one allowlisted definition rather than
 * several scattered ones.
 */
export const useA11yStyles = makeStyles({
  visuallyHidden: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: '0',
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: '0',
  },
});
